import {
    h, Component, ComponentChildren, ComponentType, createContext, createRef, RefObject
} from "preact";
import { useContext } from "preact/hooks";

import classname from "../../../foundation/utils/classname";
import { PaddingRecord } from "../../../foundation/utils/padding";
import { scrollIfNecessary } from "../../../foundation/utils/platform";
import {
    getKeyboardInfo,
    findFirstSelectedItemInRange,
    findFirstUnselectedItemInRange,
    removeItemsInRange,
    selectItemsInRange
} from "../utils/navigation";
import {
    MultiSelectGroup,
    SelectionInfo,
    withMultiSelectItem,
    WithMultiSelectItemProps
} from "..";

import styles from "./MultiSelectList.module.css";

// #region - MultiSelectList

type ScrollContainerOptions = {
    class?: string;
    ref?: RefObject<HTMLElement>;
    padding?: Partial<PaddingRecord> | number;
    offset?: number;
};

interface MultiSelectListProps {
    class?: string;
    scrollContainerOptions?: ScrollContainerOptions;
    numberOfItems: number;
    children: ComponentChildren;
    onSelectionUpdate?: (selection: Set<number>) => void;
}

interface MultiSelectListState {
    selection: Set<number>;
    lastStartIndex: number | null;
    lastEndIndex: number | null;
}

const multiSelectListInitialState: MultiSelectListState = {
    selection: new Set(),
    lastStartIndex: null,
    lastEndIndex: null
};

class MultiSelectList extends Component<MultiSelectListProps, MultiSelectListState> {
    override state: MultiSelectListState = multiSelectListInitialState;

    scrollableContainerRef = createRef<HTMLDivElement>();

    getScrollContainer = () => {
        const { ref: scrollContainerRef } = this.props.scrollContainerOptions ?? {};

        if (scrollContainerRef?.current) {
            return scrollContainerRef.current;
        }

        return this.scrollableContainerRef.current;
    };

    getRangeInfo = () => {
        const { lastStartIndex, lastEndIndex } = this.state;

        const rangeExists = lastStartIndex !== null &&
            lastEndIndex !== null &&
            lastStartIndex !== lastEndIndex;
        const rangeUpwards = rangeExists && lastEndIndex < lastStartIndex;
        const rangeDownwards = rangeExists && lastEndIndex > lastStartIndex;

        return {
            rangeExists,
            rangeUpwards,
            rangeDownwards
        };
    };

    handleUpdateSelectionState = (updatedState: Partial<MultiSelectListState>) => {
        this.setState(updatedState);
    };

    handleSelectionDone = (selectedItems: number[], selectionInfo: SelectionInfo<number>) => {
        const { lastStartIndex, lastEndIndex } = this.state;
        const selection = new Set(selectedItems);
        const { firstSelectingItem, lastSelectingItem } = selectionInfo;

        this.setState({
            selection,
            lastStartIndex: firstSelectingItem ?? lastStartIndex,
            lastEndIndex: lastSelectingItem ?? lastEndIndex
        });
    };

    handleClearSelection = () => {
        this.setState({ ...multiSelectListInitialState });
    };

    handleSelectAllItems = () => {
        const { numberOfItems } = this.props;
        const updatedSelection = new Set<number>();
        selectItemsInRange(updatedSelection, 0, numberOfItems - 1);

        this.setState({
            selection: updatedSelection,
            lastStartIndex: 0,
            lastEndIndex: numberOfItems - 1
        });
    };

    handleSelectSingleItem = (index: number) => {
        const updatedSelection = new Set<number>([index]);

        this.setState({
            selection: updatedSelection,
            lastStartIndex: index,
            lastEndIndex: null
        });

        this.handleScrollToItem(index);
    };

    handleUnselectItem = (index: number) => {
        const { selection, lastStartIndex, lastEndIndex } = this.state;
        const { rangeExists, rangeDownwards } = this.getRangeInfo();

        // Initialize updated state
        const updatedSelection = new Set(selection);
        let updatedLastStartIndex: number | null = lastStartIndex;
        let updatedLastEndIndex: number | null = lastEndIndex;

        // Unselect the item
        updatedSelection.delete(index);

        // If there is currently a selected range
        if (rangeExists) {
            const direction = rangeDownwards ? 1 : -1;
            // If last item is unselected, calculate the next "end index"
            if (index === lastEndIndex) {
                /** Example
                 * Downwards - Range: (2, 10); Unselect: 10 --> New Range: (2, 9)
                 * Upwards - Range: (10, 2); Unselect: 2 --> New Range: (10, 3)
                 */
                updatedLastEndIndex = findFirstSelectedItemInRange(
                    selection,
                    lastEndIndex - direction,
                    lastStartIndex!
                );
            }

            // If first item is unselected, calculate the next "start index"
            if (index === lastStartIndex) {
                /** Example
                 * Downwards - Range: (2, 10); Unselect: 2 --> New Range: (3, 10)
                 * Upwards - Range: (10, 2); Unselect: 10 --> New Range: (9, 2)
                 */
                updatedLastStartIndex = findFirstSelectedItemInRange(
                    selection,
                    lastStartIndex + direction,
                    lastEndIndex!
                );
            }
        }

        // If there are no items left in the selection, reset the start index
        if (updatedSelection.size === 0) {
            updatedLastStartIndex = null;
        }

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex,
            lastEndIndex: updatedLastEndIndex
        });
    };

    handleKeyboardNavigation = (ev: KeyboardEvent) => {
        const { numberOfItems } = this.props;
        const { selection, lastStartIndex, lastEndIndex } = this.state;
        const { upPressed, shiftPressed } = getKeyboardInfo(ev);

        // When there is no current selection
        if (lastStartIndex === null) {
            const index = upPressed ? numberOfItems - 1 : 0;
            this.handleSelectSingleItem(index);
            return;
        }

        const lastIndex = lastEndIndex ?? lastStartIndex;
        const upwardsIndex = Math.max(lastIndex - 1, 0);
        const downwardsIndex = Math.min(lastIndex + 1, numberOfItems - 1);

        let index: number | null = upPressed ? upwardsIndex : downwardsIndex;

        let updatedSelection = new Set<number>();
        let updatedLastStartIndex: number | null = index;
        let updatedLastEndIndex: number | null = null;

        if (shiftPressed) {
            index = upPressed
                ? findFirstUnselectedItemInRange(selection, upwardsIndex, 0)
                : findFirstUnselectedItemInRange(selection, downwardsIndex, numberOfItems - 1);

            // If there is no item to add to the range, update the end index of the range
            if (index === null) {
                updatedLastEndIndex = upPressed ? 0 : numberOfItems - 1;

                this.setState({
                    lastEndIndex: updatedLastEndIndex
                });

                this.handleScrollToItem(updatedLastEndIndex);
                return;
            }

            updatedSelection = new Set(selection);
            updatedLastStartIndex = lastStartIndex;
            updatedLastEndIndex = index;
        }

        updatedSelection.add(index);

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex,
            lastEndIndex: updatedLastEndIndex
        });

        this.handleScrollToItem(index);
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        const { numberOfItems } = this.props;
        const { selection, lastStartIndex, lastEndIndex } = this.state;

        const {
            upPressed,
            downPressed,
            shiftPressed,
            cmdPressed,
            altPressed,
            aPressed
        } = getKeyboardInfo(ev);

        // Handle select all shortcut
        if (cmdPressed && aPressed) {
            ev.preventDefault();
            this.handleSelectAllItems();
            return;
        }

        if (!upPressed && !downPressed) {
            return;
        }

        ev.preventDefault();

        // Selecting only the fist/last item
        if (cmdPressed && altPressed && !shiftPressed) {
            const itemIndex = upPressed ? 0 : numberOfItems - 1;
            this.handleSelectSingleItem(itemIndex);
            return;
        }

        // Selecting to the start/end of the list
        if (shiftPressed && altPressed && lastStartIndex !== null) {
            const startIndex = lastEndIndex ?? lastStartIndex;
            const endIndex = upPressed ? 0 : numberOfItems - 1;

            const updatedSelection = new Set<number>();
            selectItemsInRange(updatedSelection, startIndex, endIndex);

            this.setState({
                selection: updatedSelection,
                lastStartIndex: startIndex,
                lastEndIndex: endIndex
            });

            this.handleScrollToItem(endIndex);
            return;
        }

        const { rangeUpwards, rangeDownwards } = this.getRangeInfo();

        // Unselecting the last item in range
        const unselectUpwards = rangeDownwards && upPressed;
        const unselectDownwards = rangeUpwards && downPressed;
        const unselectLastItemInRange = shiftPressed && (unselectUpwards || unselectDownwards);

        if (unselectLastItemInRange) {
            // Remove last item in range
            const updatedSelection = new Set(selection);
            updatedSelection.delete(lastEndIndex!);

            // Find new index of the last item in range
            const updatedLastEndIndex = unselectUpwards
                ? findFirstSelectedItemInRange(selection, lastEndIndex! - 1, lastStartIndex!)
                : findFirstSelectedItemInRange(selection, lastEndIndex! + 1, lastStartIndex!);

            this.setState({
                selection: updatedSelection,
                lastEndIndex: updatedLastEndIndex
            });

            this.handleScrollToItem(updatedLastEndIndex!);
            return;
        }

        this.handleKeyboardNavigation(ev);
    };

    handleScrollToItem = (index: number) => {
        const { scrollContainerOptions } = this.props;
        const { padding, offset } = scrollContainerOptions ?? {};

        const container = this.getScrollContainer();
        const element = container?.querySelector(`[data-index="${index}"]`);

        scrollIfNecessary(element, { container, padding, offset });
    };

    handleScrollContainerFocus = () => {
        document.addEventListener("keydown", this.handleKeyDown);
    };

    handleScrollContainerBlur = () => {
        document.removeEventListener("keydown", this.handleKeyDown);
    };

    handleDocumentMouseDown = (ev: MouseEvent) => {
        const { cmdPressed, shiftPressed } = getKeyboardInfo(ev);
        if (cmdPressed || shiftPressed) {
            return;
        }

        this.handleClearSelection();
    };

    handleMultiSelectListItemMouseDown = (ev: MouseEvent) => {
        // Prevent the selection to start from directly on an item
        ev.stopPropagation();
    };

    handleMultiSelectListItemClick = (ev: MouseEvent, index: number) => {
        const { selection, lastStartIndex, lastEndIndex } = this.state;
        const { cmdPressed, shiftPressed } = getKeyboardInfo(ev);
        const { rangeExists, rangeUpwards, rangeDownwards } = this.getRangeInfo();

        if (cmdPressed && selection.has(index)) {
            this.handleUnselectItem(index);
            return;
        }

        // Initialize updated state
        let updatedSelection = new Set<number>();
        let updatedLastStartIndex: number | null = index;
        let updatedLastEndIndex: number | null = null;

        // When cmd or shift is pressed, we want to include the previous selection
        if (cmdPressed || shiftPressed) {
            updatedSelection = new Set<number>(selection);
        }

        updatedSelection.add(index);

        const expandRangeUpwards = rangeUpwards && index === lastEndIndex! - 1;
        const expandRangeDownwards = rangeDownwards && index === lastEndIndex! + 1;

        const flipRangeUpwards = rangeDownwards && index === lastStartIndex! - 1;
        const flipRangeDownwards = rangeUpwards && index === lastStartIndex! + 1;

        if (cmdPressed && (expandRangeUpwards || expandRangeDownwards)) {
            /** Example
             * Downwards - Range: (2, 10); Clicked: 11 --> New range: (2, 11)
             * Upwards - Range: (10, 2); Clicked: 1 --> New range: (10, 1)
             */
            const direction = expandRangeDownwards ? 1 : -1;

            updatedLastStartIndex = lastStartIndex;
            updatedLastEndIndex = lastEndIndex! + direction;
        } else if (cmdPressed && (flipRangeUpwards || flipRangeDownwards)) {
            /** Example
             * Downwards - Range: (2, 10); Clicked: 1 --> New range: (1, 10)
             * Upwards Range: (10, 2); Clicked: 11 --> New range: (11, 2)
             */
            const direction = flipRangeDownwards ? 1 : -1;

            updatedLastStartIndex = lastEndIndex;
            updatedLastEndIndex = lastStartIndex! + direction;
        } else if (shiftPressed) {
            // Select the range
            if (rangeExists) {
                removeItemsInRange(updatedSelection, lastStartIndex!, lastEndIndex!);
            }

            selectItemsInRange(updatedSelection, lastStartIndex ?? 0, index);

            updatedLastStartIndex = lastStartIndex;
            updatedLastEndIndex = index;
        }

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex,
            lastEndIndex: updatedLastEndIndex
        });
    };

    override componentDidMount() {
        document.addEventListener("mousedown", this.handleDocumentMouseDown);
    }

    override componentDidUpdate(_: MultiSelectListProps, prevState: MultiSelectListState) {
        const { onSelectionUpdate } = this.props;
        const { selection: prevSelection } = prevState;
        const { selection } = this.state;

        if (prevSelection !== selection) {
            onSelectionUpdate?.(selection);
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleDocumentMouseDown);
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    render() {
        const { class: cssClass, scrollContainerOptions, children } = this.props;
        const { selection } = this.state;

        const { class: scrollContainerClass } = scrollContainerOptions ?? {};

        const contextValue: MultiSelectListContext = {
            onMultiSelectListItemClick: this.handleMultiSelectListItemClick,
            onMultiSelectListItemMouseDown: this.handleMultiSelectListItemMouseDown
        };

        const selectedItems = [...selection];

        return (
            <MultiSelectListContext.Provider value={contextValue}>
                <MultiSelectGroup
                    class={classname(cssClass)}
                    selectedIds={selectedItems}
                    allowCtrlClick
                    deselectOnEsc
                    enableDeselect
                    getScrollContainer={this.getScrollContainer}
                    onSelectionDone={this.handleSelectionDone}
                    onClearSelection={this.handleClearSelection}>
                    <div
                        tabIndex={0}
                        class={classname(styles.scrollContainer, scrollContainerClass)}
                        ref={this.scrollableContainerRef}
                        onFocus={this.handleScrollContainerFocus}
                        onBlur={this.handleScrollContainerBlur}>
                        {children}
                    </div>
                </MultiSelectGroup>
            </MultiSelectListContext.Provider>
        );
    }
}

// #endregion

// #region - MultiSelectListContext

interface MultiSelectListContext {
    onMultiSelectListItemClick: (ev: MouseEvent, index: number) => void;
    onMultiSelectListItemMouseDown: (ev: MouseEvent, index: number) => void;
}

const MultiSelectListContextDefaultValue: MultiSelectListContext = {
    onMultiSelectListItemClick: () => { /* No-op */ },
    onMultiSelectListItemMouseDown: () => { /* No-op */ }
};

const MultiSelectListContext = createContext<MultiSelectListContext>(MultiSelectListContextDefaultValue);
const useMultiSelectListContext = (): MultiSelectListContext => useContext(MultiSelectListContext)!;

// #endregion

// #region - withMultiSelectListItem

type MultiSelectListItemRequiredProps = WithMultiSelectItemProps<number>;

interface MultiSelectListItemInjectedProps {
    onMouseDown: (ev: MouseEvent) => void;
    onClick: (ev: MouseEvent) => void;
}

type WithMultiSelectListItemProps = MultiSelectListItemRequiredProps & MultiSelectListItemInjectedProps;

/**
 * P is the type of own props of the wrapped component
 */
function withMultiSelectListItem<P extends MultiSelectListItemRequiredProps>(
    WrappedComponent: ComponentType<P>
) {
    return withMultiSelectItem((props: Omit<P, keyof MultiSelectListItemInjectedProps>) => {
        const { selectableId: index } = props;
        const { onMultiSelectListItemClick, onMultiSelectListItemMouseDown } = useMultiSelectListContext();

        function handleMouseDown(ev: MouseEvent) {
            onMultiSelectListItemMouseDown(ev, index);
        }

        function handleClick(ev: MouseEvent) {
            onMultiSelectListItemClick(ev, index);
        }

        return (
            <WrappedComponent
                {...props as P}
                onMouseDown={handleMouseDown}
                onClick={handleClick}/>
        );
    });
}

// #endregion

export {
    MultiSelectList,
    withMultiSelectListItem,
    WithMultiSelectListItemProps
};
